import React, {useMemo} from "react";
import PropTypes from "prop-types";
import {Picture} from "../../components/Picture";
import {i18nFormat} from "../../lib/i18nFormat";
import {urlToPage} from "@shared/lib/urlToPage";
import {deviceLicenses} from "../../enums/deviceLicenses";
import {deviceModels} from "../../enums/deviceModels";

export function Device(props) {
  const urlToUsbTool = "https://usb.homey.app";
  const homeyPro2019Image = "/img/pages/account/devices/homey-pro-2019.png";
  const homeyProImage = "/img/pages/account/devices/homey-pro.png";
  const homeyProMiniImage = "/img/pages/account/devices/homey-pro-mini.png";
  const homeyBridgeImage = "/img/pages/account/devices/homey-bridge.png";
  const homeyEnergyDongleImage = "/img/pages/account/devices/homey-energy-dongle.png";

  const deviceActions = useMemo(() => {
    const usbToolModels = [deviceModels.HY0038, deviceModels.homey5q, deviceModels.homey6q, deviceModels.homey7q];
    const actions = [];

    if (usbToolModels.includes(props.device.model)) {
      actions.push("usb-tool");
    }
    actions.push("support");
    return actions;
  }, [props.device.model]);

  function getImage() {
    switch (props.device.model) {
      case deviceModels.HY0016:
        return (
          <Picture
            className="account-device__image"
            src={homeyBridgeImage}
            alt="Homey Bridge"
          />
        );
      case deviceModels.HY0038:
        return (
          <Picture
            className="account-device__image"
            src={homeyEnergyDongleImage}
            alt="Homey Energy Dongle"
          />
        );
      case deviceModels.homey1s:
      case deviceModels.homey1d:
      case deviceModels.homey1q:
      case deviceModels.homey2s:
      case deviceModels.homey2d:
      case deviceModels.homey2q:
      case deviceModels.homey3s:
      case deviceModels.homey3d:
      case deviceModels.homey4d:
        return (
          <Picture
            className="account-device__image"
            src={homeyPro2019Image}
            alt="Homey Pro (2016 — 2019)"
          />
        );
      case deviceModels.homey5q:
      case deviceModels.homey7q:
        return (
          <Picture
            className="account-device__image"
            src={homeyProImage}
            alt="Homey Pro"
          />
        );
      case deviceModels.homey6q:
        return (
          <Picture
            className="account-device__image"
            src={homeyProMiniImage}
            alt="Homey Pro mini"
          />
        );
      default:
        return null;
    }
  }

  function getTitle() {
    switch (props.device.model) {
      case deviceModels.HY0016:
        return "Homey Bridge";
      case deviceModels.HY0038:
        return "Homey Energy Dongle";
      case deviceModels.homey1s:
      case deviceModels.homey1d:
      case deviceModels.homey1q:
      case deviceModels.homey2s:
      case deviceModels.homey2d:
      case deviceModels.homey2q:
      case deviceModels.homey3s:
      case deviceModels.homey3d:
      case deviceModels.homey4d:
      case deviceModels.homey5q:
      case deviceModels.homey6q:
      case deviceModels.homey7q:
      default:
        return props.device.name;
    }
  }

  function getDetails() {
    switch (props.device.model) {
      case deviceModels.HY0016:
        return i18nFormat("devices.details.connected", { homeyName: props.parent.name, deviceMac: props.device.mac });
      case deviceModels.HY0038:
        return i18nFormat("devices.details.connected", { homeyName: props.parent.name, deviceMac: props.device.mac });
      case deviceModels.homey1s:
      case deviceModels.homey1d:
      case deviceModels.homey1q:
      case deviceModels.homey2s:
      case deviceModels.homey2d:
      case deviceModels.homey2q:
      case deviceModels.homey3s:
      case deviceModels.homey3d:
      case deviceModels.homey4d:
        return `Homey Pro (2016 — 2019) — ${props.device.softwareVersion}`;
      case deviceModels.homey5q:
      case deviceModels.homey6q:
      case deviceModels.homey7q:
      default:
        return `${props.device.modelName} — ${props.device.softwareVersion}`;
    }
  }

  function getLicense(license) {
    switch (license) {
      case deviceLicenses.advancedFlow:
        return (
          <span key={license}
                className="account-device-license">{i18nFormat("devices.licenses.advanced-flow")}</span>
        )
      case deviceLicenses.moods:
        return (
          <span key={license}
                className="account-device-license">{i18nFormat("devices.licenses.moods")}</span>
        )
      case deviceLicenses.dashboards:
        return (
          <span key={license}
                className="account-device-license">{i18nFormat("devices.licenses.dashboards")}</span>
        )
      default:
        return null;
    }
  }

  return (
    <div className="account-device">
      <div className="account-device__component">
        {getImage()}
        <div className="account-device__content">
          <div className="account-device__info">
            <h3 className="account-device__name">{getTitle()}</h3>
            <div className="account-device__details">
              {getDetails()}
            </div>
            <div className="account-device__licenses">
              {props.device.licenses && props.device.licenses.map((license) => getLicense(license))}
            </div>
          </div>
          <div className="account-device__footer">
            {deviceActions.includes("usb-tool") && (
              <a href={urlToUsbTool} className="account-device-action-button">
                <span>{i18nFormat("devices.link-to-usb-tool")}</span>
                <span className="glyph-core-square-arrow-up-right"/>
              </a>
            )}
            {deviceActions.includes("support") && (
              <a href={urlToPage("support-form")} className="account-device-action-button">
                <span>{i18nFormat("devices.link-to-support")}</span>
                <span className="glyph-core-square-arrow-up-right"/>
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

Device.propTypes = {
  device: PropTypes.object.isRequired,
  parent: PropTypes.object,
}